import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { BookOpen, Trash2 } from 'lucide-react';
import axios from 'axios';
import { API_ENDPOINTS } from '@/constants';
import { courseAPI } from '@/services/api';
import CoursePreviewModal from '@/components/CoursePreviewModal';

/**
 * My Courses Component
 * Shows user's course history with preview matching Step 4 style
 */
const MyCourses = () => {
  const { t } = useTranslation();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal state
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadCourses();
  }, []);

  // Handle course card click - fetch full data and open modal
  const handleCardClick = async (courseId) => {
    setSelectedCourseId(courseId);
    setModalOpen(true);
    setModalLoading(true);
    try {
      const fullCourse = await courseAPI.getById(courseId);
      setSelectedCourse(fullCourse);
    } catch (error) {
      console.error('Error fetching course:', error);
      setSelectedCourse(null);
    } finally {
      setModalLoading(false);
    }
  };

  // Handle modal close - reset state
  const handleModalClose = () => {
    setModalOpen(false);
    // Delay clearing data so close animation completes
    setTimeout(() => {
      setSelectedCourse(null);
      setSelectedCourseId(null);
    }, 200);
  };

  const handleDeleteCourse = async (e, courseId, courseTitle) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${courseTitle || 'this course'}"? This action cannot be undone.`)) {
      return;
    }
    setDeletingId(courseId);
    try {
      await courseAPI.delete(courseId);
      setCourses(prev => prev.filter(c => c.id !== courseId));
    } catch (error) {
      console.error('Error deleting course:', error);
      alert('Failed to delete course. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const loadCourses = async () => {
    try {
      setLoading(true);
      const response = await axios.get(API_ENDPOINTS.COURSES);
      setCourses(response.data);
    } catch (error) {
      console.error('Error loading courses:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('app.loadingCourses')}</p>
        </div>
      </div>
    );
  }

  // Show course list
  return (
    <div>
      <div className="mb-8">
        <h2 className="text-3xl font-bold bg-gradient-to-r from-orange-600 via-pink-600 to-teal-600 bg-clip-text text-transparent mb-2">
          {t('myCourses.title')}
        </h2>
        <p className="text-gray-600">{t('myCourses.viewCourses')}</p>
      </div>

      {courses.length === 0 ? (
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('myCourses.noCourses')}</h3>
          <p className="text-gray-500">{t('myCourses.createFirstCourse')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => {
            const totalActivities = course.topics?.reduce((sum, topic) =>
              sum + (topic.activities?.length || 0), 0
            ) || 0;

            return (
              <div
                key={course.id}
                onClick={() => handleCardClick(course.id)}
                className="bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border-2 border-gray-100 overflow-hidden cursor-pointer hover:border-teal-200"
              >
                {/* Card header with gradient */}
                <div className="h-2 bg-gradient-to-r from-orange-500 via-pink-500 to-teal-500"></div>

                <div className="p-6 relative">
                  <button
                    onClick={(e) => handleDeleteCourse(e, course.id, course.title)}
                    disabled={deletingId === course.id}
                    className="absolute top-4 right-4 p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 transition-all duration-200 z-10"
                    title="Delete course"
                  >
                    <Trash2 size={18} className={deletingId === course.id ? 'animate-pulse' : ''} />
                  </button>
                  <h3 className="text-xl font-bold text-gray-800 mb-2 line-clamp-2 pr-10">
                    {course.title}
                  </h3>
                  <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                    {course.description || t('common.noDescription')}
                  </p>

                  <div className="grid grid-cols-3 gap-2 mb-4">
                    <div className="text-center p-2 bg-orange-50 rounded-lg">
                      <div className="text-2xl font-bold text-orange-600">
                        {course.learningObjectives?.length || course.goals?.length || 0}
                      </div>
                      <div className="text-xs text-gray-600">{t('myCourses.goals')}</div>
                    </div>
                    <div className="text-center p-2 bg-teal-50 rounded-lg">
                      <div className="text-2xl font-bold text-teal-600">{course.topics?.length || 0}</div>
                      <div className="text-xs text-gray-600">{t('myCourses.topics')}</div>
                    </div>
                    <div className="text-center p-2 bg-pink-50 rounded-lg">
                      <div className="text-2xl font-bold text-pink-600">{totalActivities}</div>
                      <div className="text-xs text-gray-600">{t('myCourses.activities')}</div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between text-sm mb-4">
                    <span className="text-gray-600">{course.subjectArea || t('common.noSubject')}</span>
                    <span className="text-teal-600 font-semibold">{course.totalHours || 0}h</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Course Preview Modal */}
      <CoursePreviewModal
        isOpen={modalOpen}
        onClose={handleModalClose}
        course={selectedCourse}
        isLoading={modalLoading}
        courseId={selectedCourseId}
      />
    </div>
  );
};

export default MyCourses;



